/* Copyright (c) 2020-present CloudIO Inc */

import { getManager } from '../../registry/Manager';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { AliasType, ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { CloudDataStoreItem, defaultValue } from './CloudDataStore';
import {
  controlledBy,
  displayName,
  dsAppUidProp,
  getNextAlias,
  hideDirtyIndicator,
} from '../DataStore/DataStoreManager';
import { camelCase } from 'lodash-es';
import { emptyFunction } from '@cloudio-saas/statex';

const CloudDataStoreManager: ComponentManagerFn<'CloudDataStore'> = (
  platform: Platform,
) => {
  let timer: NodeJS.Timeout;
  let callback = emptyFunction;
  return {
    ...baseComponentManager<'CloudDataStore'>(platform, 'CloudDataStore'),
    type: 'CloudDataStore',
    name: 'Cloud Data Store',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'TriggerAfterLoad',
          'TriggerAfterSave',
          'TriggerBeforeCreate',
          'TriggerBeforeLoad',
          'TriggerBeforeSave',
          'TriggerOnInitialize',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      dsAppUid: dsAppUidProp(platform),
      dataSource: {
        label: 'Cloud Function',
        type: 'FunctionURIChooser',
        required: true,
        refreshCallback(cb) {
          callback = cb;
        },
        getAppUid(page, item, context) {
          return (item as CloudDataStoreItem).props.dsAppUid;
        },
      },
      alias: {
        label: 'Data Source Alias',
        type: 'DataSourceAlias',
        required: true,
      },
      displayName,
      jsonData: {
        label: 'JSON Data',
        type: 'json',
        required: true,
      },
      insertable: {
        label: 'Insertable',
        type: 'booleanExpression',
      },
      updatable: {
        label: 'Updatable',
        type: 'booleanExpression',
      },
      deletable: {
        label: 'Deletable',
        type: 'boolean',
      },
      autoQuery: {
        label: 'Auto Query',
        type: 'boolean',
      },
      filterLocally: {
        label: 'Filter Locally',
        type: 'boolean',
      },
      singleton: {
        label: 'Singleton',
        type: 'boolean',
        helpText:
          'Singleton dataStores defined across different pages, with the same alias, will share the same state.',
      },
      cacheAll: {
        label: 'Cache All',
        type: 'boolean',
        helpText:
          'Check this to cache query result. With cache enabled, duplicate queries are not sent to the server.',
      },
      ignorePKDuplicate: {
        label: 'Ignore Primary Keys Duplicates',
        type: 'boolean',
        helpText:
          'Check this to ignore the duplicate primary key values on this store.',
      },
      controlledBy,
      hideDirtyIndicator,
    },
    isPropertyVisible: (property, item) => {
      switch (property) {
        case 'dsAppUid':
        case 'dataSource':
          return true;
        case 'alias':
          return !!item.dataSource;
        case 'jsonData':
          return !!item.alias;
        default:
          return !!item.alias;
      }
    },
    onPropertyChange: (property, value, itemProps, { appUid, pageId }) => {
      if (property === 'dsAppUid') {
        callback();
        return {
          [property]: value,
          dataSource: '',
        };
      } else if (property === 'dataSource' && typeof value === 'string') {
        const map = platform.getDataSources(appUid, pageId);
        const val = getNextAlias(`${camelCase(value)}Alias`, map);
        if (timer) {
          clearTimeout(timer);
        }
        timer = setTimeout(() => {
          platform.resetAliases(appUid, pageId);
        }, 1300);
        return {
          [property]: value,
          alias: val,
        };
      }
      return {
        [property]: value,
      };
    },
    onCopy: (props, samePage) => ({
      ...props,
      alias: samePage ? '' : props.alias,
    }),
    getDataSourceAliasProperty: (type: AliasType) =>
      type === 'STORE' || type === 'QUERY_FILTER'
        ? { aliasProp: 'alias' }
        : undefined,
    getDisplayText: (item, expanded, { appUid, pageId }) => {
      if (!item.props.alias) return '';
      const page = platform.getPage(appUid, pageId);
      const itemIds = Object.keys(page);
      const usage = itemIds.filter((id) => {
        const itm = page[id];
        const mgr = getManager(itm.type);
        return mgr.isDataSourceAliasUsed(itm, item.props.alias);
      });
      return usage.length
        ? `${item.props.alias} (${usage.length} usage${
            usage.length > 1 ? 's' : ''
          })`
        : item.props.alias;
    },
    generateShortId: (item) => item.alias,
    replaceableWith: ['LocalDataStore'],
    category: 'Data',
    icon: { iconName: 'database' },
    description: `**Overview**<br/>
The Cloud Data Store is a logical, non-visual component that serves as the bridge between your user interface and your backend logic. Specifically, it connects your page to a Cloud Function (REST API), fetches the data returned by that function, and holds it in a state that other UI components (like Charts, Grids, or Forms) can consume.
Think of it as an "invisible engine" running in the background of your page. It manages the complexity of API calls, data caching, and state synchronization so your visual components don't have to.

**1. Core Configuration**<br/>
To make data available to your page, you must define where it comes from and what it looks like.
* **Cloud Function:** Select the backend function (API endpoint) that acts as the source of truth. This function is responsible for returning the JSON data.
* **Data Source Alias:** A unique "nickname" for this store (e.g., "userData" or "ordersList"). This is the handle you will use in other components to bind data (e.g., setting a Grid's data source to "userData").
* **JSON Data:** Defines the expected structure (schema) of the data. This acts as a blueprint, allowing the designer to know which fields (like id, name, status) are available for binding even before the data loads.

**2. Data Lifecycle & Behavior**<br/>
You can control exactly how and when data is loaded to optimize performance and user experience.
* **Auto Query:**
    * **Enabled:** The store automatically calls the Cloud Function as soon as the page loads. Best for primary content like dashboards.
    * **Disabled:** The store waits until explicitly triggered (e.g., by a button click or another event). Best for "on-demand" data like drill-down details.
* **Singleton:**
    * **Enabled:** The store's data is shared globally across the application. If multiple pages use a Cloud Data Store with the exact same Alias, they will share the same cached data state. This prevents unnecessary API calls when navigating between tabs.
* **Cache All:** Enables client-side caching. If the same query is requested again, the store serves the cached result instead of hitting the server, significantly speeding up the UI.

**3. Permissions & Control (CRUD)**<br/>
The Cloud Data Store acts as a security gatekeeper for your frontend data operations. You can define boolean expressions to dynamically enable or disable capabilities.
* **Insertable / Updatable / Deletable:** Expressions that return true or false. Use these to enforce role-based access control (RBAC).
* **Example:** Set Deletable to \`$.user.role === 'Admin'\`. If a regular user views the page, any "Delete" buttons connected to this store will automatically be disabled or hidden.

**4. Triggers & Hooks**<br/>
Since the Cloud Data Store manages the flow of data, it exposes powerful lifecycle hooks to run logic at specific moments.
* **Supported Children:**
    * **TriggerBeforeLoad / TriggerAfterLoad:** Run logic immediately before fetching data (e.g., "Show Loading Spinner") or after data arrives (e.g., "Hide Spinner", "Show Toast Notification").
    * **TriggerBeforeSave / TriggerAfterSave:** Intercept save operations to validate data or confirm success.
    * **TriggerOnInitialize:** Runs once when the store is first created.`,
  };
};

export default CloudDataStoreManager;
